// @ts-check
// resizable-panel.js — Drag-handle on the side panel's left edge.
//
// Lets desktop users widen / narrow the side panel and persists the chosen
// width to localStorage.  Disabled on touch (the panel is a swipe drawer
// there) and on narrow desktop windows where the panel is hidden — those
// modes are gated by CSS in panels.css.
//
// Pre-paint hydration of `--panel-width` lives inline in index.html so the
// first frame paints with the user's saved width.  This module owns the
// live drag interaction, the localStorage write on drag-end, and the
// window-resize re-clamp.
//
// Width policy:
//   • The user's saved width is the source of truth (localStorage).
//   • The displayed width is `max(MIN_WIDTH, min(saved, cap))` where the
//     cap is `min(MAX_WIDTH, viewport − EDITOR_MIN_WIDTH − 2·gap)`.  This
//     guarantees the editor always has at least EDITOR_MIN_WIDTH of usable
//     space, regardless of how wide the user previously dragged the panel.
//   • Window resize re-clamps the display without writing back, so a
//     temporary narrow window doesn't destroy the user's preferred width.
//   • Drag input is clamped to the same cap, so the user can't drag past
//     the editor minimum.
//
// Was: references core/app-state.js's `panelLists` + `mobileMediaQuery`
// classic-script globals. Phase 4.5: resolved locally via the DOM /
// matchMedia (the IIFE stays — it's still valid inside an ES module and
// scopes the resizer state).

import { scheduleHidePanel, showPanel } from './panels.js';
import { coarsePointerQuery } from '../utils/coarse-pointer.js';

(() => {
  const panelLists = document.getElementById('panel-lists') as any;
  if (!panelLists) return;

  const STORAGE_KEY = 'panel_width';
  const MIN_WIDTH = 240;
  const MAX_WIDTH = 600;
  // Reserve at least this much horizontal space for the editor pane.
  // 400 px gives ~50 chars/line at the 16 px editor font — tight but legible.
  const EDITOR_MIN_WIDTH = 400;

  /** @returns {number} the max panel width given the viewport + editor minimum. */
  function getCap() {
    const gap =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--gap'), 10) || 10;
    return Math.min(MAX_WIDTH, window.innerWidth - EDITOR_MIN_WIDTH - 2 * gap);
  }

  /**
   * @param {number} px
   * @returns {number}
   */
  function clamp(px: any) {
    return Math.max(MIN_WIDTH, Math.min(getCap(), px));
  }

  /**
   * @param {number} px
   * @returns {number} the clamped width that was applied.
   */
  function applyWidth(px: any) {
    const clamped = clamp(px);
    document.documentElement.style.setProperty('--panel-width', `${clamped}px`);
    return clamped;
  }

  const resizer = document.createElement('div');
  resizer.id = 'panel-resizer';
  resizer.setAttribute('role', 'separator');
  resizer.setAttribute('aria-orientation', 'vertical');
  resizer.setAttribute('aria-label', 'Resize side panel');
  document.body.appendChild(resizer);

  let dragging = false;
  /** @type {any} */
  let pointerId: any = null;
  let grabOffset = 0;
  /** @type {any} */
  let lastWidth: any = null;

  const statusPill = document.getElementById('bottom-status-area');

  // Re-clamp the displayed width whenever the saved preference is exceeded
  // by the current cap.  This runs at module load (after the inline pre-
  // paint hydration) and on every window resize.  We never write back to
  // localStorage here — the user's preferred width is preserved for when
  // the window grows again.
  /** @returns {void} */
  function reapplyFromSaved() {
    const saved = parseInt(localStorage.getItem(STORAGE_KEY) as any, 10);
    if (!Number.isFinite(saved)) return;
    const display = clamp(saved);
    const current =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--panel-width')) || 0;
    if (Math.round(current) === display) return;
    document.documentElement.style.setProperty('--panel-width', `${display}px`);
  }
  reapplyFromSaved();

  // requestAnimationFrame coalescing: window 'resize' fires up to 60+
  // times/sec on snap, and reapplyFromSaved reads getComputedStyle which
  // forces a reflow. With this guard we run at most once per frame.
  let _resizeRaf = 0;
  /** @returns {void} */
  function _onResize() {
    if (_resizeRaf) return;
    _resizeRaf = requestAnimationFrame(() => {
      _resizeRaf = 0;
      reapplyFromSaved();
    });
  }
  window.addEventListener('resize', _onResize);

  resizer.addEventListener('pointerdown', (e) => {
    if (coarsePointerQuery.matches) return;
    if (e.button !== undefined && e.button !== 0) return;
    dragging = true;
    pointerId = e.pointerId;
    try {
      resizer.setPointerCapture(pointerId);
    } catch {
      /* unsupported */
    }
    document.body.classList.add('panel-resizing');
    // The status pill's `left` has a 0.3s transition (toolbar.css) so that
    // pin-state changes glide.  During a drag we want the pill to track
    // the panel edge in real time, so suppress the position transition
    // for the duration of the drag.
    statusPill?.classList.add('no-position-transition');
    // Preserve the click position relative to the panel's visual left edge
    // so the panel doesn't jump on pointerdown.
    const rect = panelLists.getBoundingClientRect();
    grabOffset = e.clientX - rect.left;
    e.preventDefault();
  });

  // rAF-coalesce pointermove handling so high-refresh pointer streams
  // (120 Hz+ on modern trackpads) collapse into one DOM write per frame.
  // The previous implementation ran a getBoundingClientRect() read +
  // CSS write on every event, causing visible jank during fast drags.
  /** @type {any} */
  let _pendingClientX: any = null;
  let _pendingResizeRaf = 0;
  /** @returns {void} */
  function _flushPendingResize() {
    _pendingResizeRaf = 0;
    if (_pendingClientX === null || !dragging) return;
    const x = _pendingClientX;
    _pendingClientX = null;
    const rect = panelLists.getBoundingClientRect();
    // Panel right edge is invariant (anchored to viewport via right:0).
    // New panel width = right edge - desired new left edge.
    const desired = rect.right - (x - grabOffset);
    lastWidth = applyWidth(desired);
  }
  resizer.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    _pendingClientX = e.clientX;
    if (_pendingResizeRaf === 0) {
      _pendingResizeRaf = requestAnimationFrame(_flushPendingResize);
    }
  });

  /** @returns {void} */
  function endDrag() {
    if (!dragging) return;
    dragging = false;
    document.body.classList.remove('panel-resizing');
    // Restore the status-pill's position transition on the next frame so
    // any pending positional change settles instantly rather than gliding.
    requestAnimationFrame(() => statusPill?.classList.remove('no-position-transition'));
    if (pointerId !== null) {
      try {
        resizer.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
      pointerId = null;
    }
    if (lastWidth !== null) {
      try {
        localStorage.setItem(STORAGE_KEY, String(lastWidth));
      } catch {
        /* quota */
      }
    }
  }

  resizer.addEventListener('pointerup', endDrag);
  resizer.addEventListener('pointercancel', endDrag);
  resizer.addEventListener('lostpointercapture', endDrag);

  // Double-click resets to the default (clears the saved width).
  resizer.addEventListener('dblclick', () => {
    document.documentElement.style.removeProperty('--panel-width');
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    lastWidth = null;
  });

  // Hovering the resizer should keep an unpinned panel open — without
  // this, mouseleave on #panel-lists would auto-hide the panel as soon
  // as the cursor crosses onto the resizer.
  resizer.addEventListener('mouseenter', () => showPanel(true));
  resizer.addEventListener('mouseleave', () => scheduleHidePanel());
})();
